import type { ComponentProps, InputHTMLAttributes, ReactElement } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { useTheme } from '@emotion/react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { validator } from '@twistopayments/design-system/form';

import { FormHelperText } from '../../atoms/form-helper-text';
import { Typography } from '../../atoms/typography';

import { useCheckboxGroupControl } from './checkbox-group/checkbox-group.context';
import { CheckboxIcon } from './checkbox-icon';
import * as styles from './checkbox.styles';

export type CheckboxVariant = 'default' | 'smaller';
export type CheckboxSize = 'medium' | 'small';

type Props = {
  name: string;
  helperText?: string;
  label: ReactElement | ReactElement[];
  labelTypographyProps?: ComponentProps<typeof Typography>;
  indeterminate?: boolean;
  error?: boolean;
  className?: string;
  variant?: CheckboxVariant;
  size?: CheckboxSize;
  disabled?: boolean;
  required?: boolean;
  hideAsterisk?: boolean;
  endAdornment?: ReactElement;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'pattern' | 'required' | 'size'>;

export const Checkbox = ({
  label,
  labelTypographyProps,
  name,
  error: errorProp = false,
  id,
  indeterminate = false,
  disabled = false,
  helperText,
  className,
  variant = 'default',
  size,
  autoFocus = false,
  required = false,
  hideAsterisk = false,
  endAdornment,
  ...other
}: Props) => {
  const theme = useTheme();
  const checkboxRef = useRef<HTMLInputElement | null>(null);
  const checkboxGroupCtx = useCheckboxGroupControl();
  // cast to ensure missing provider is handled
  const { register, watch, formState, clearErrors } =
    (useFormContext() as UseFormReturn | null) || {};

  const fieldErrors = useMemo(() => formState?.errors ?? {}, [formState?.errors]);

  const isChecked = watch ? Boolean(watch(name)) : checkboxRef.current?.checked ?? false;

  const hasError = errorProp || Boolean(fieldErrors[name]);

  useEffect(() => {
    if (isChecked) {
      clearErrors?.(name);
    }
  }, [clearErrors, fieldErrors, isChecked, name]);

  useEffect(() => {
    if (checkboxGroupCtx) {
      checkboxGroupCtx.registerCheckbox(name);
    }
  }, [checkboxGroupCtx, name]);

  useEffect(() => {
    if (checkboxRef.current && autoFocus) {
      checkboxRef.current.focus();
    }
  }, [autoFocus]);

  const rules = {
    required: required ? validator.getValidationMessages().required : undefined,
  };

  const formProps = register?.(name, {
    ...rules,
    disabled,
    ...other,
  });

  const setRef = (ref: HTMLInputElement | null) => {
    checkboxRef.current = ref;
    formProps?.ref(ref);
  };

  const resolvedSize = size ?? (variant === 'smaller' ? 'small' : 'medium');
  const labelTextStyles =
    resolvedSize === 'small' ? styles.smallerLabelText(theme) : styles.defaultLabelText(theme);

  return (
    <div className={className} css={styles.root(theme)}>
      <label css={styles.label} htmlFor={id ?? name}>
        <div>
          <input
            css={[styles.input(theme), hasError && styles.inputError(theme)]}
            data-testid="checkbox"
            disabled={disabled}
            id={id ?? name}
            name={name}
            type="checkbox"
            {...other}
            {...formProps}
            ref={setRef}
          />

          <CheckboxIcon
            css={styles.checkboxIcon(theme)}
            error={hasError}
            isChecked={isChecked}
            isDisabled={disabled}
            isIndeterminate={indeterminate}
          />
        </div>
        <div css={styles.content(theme, resolvedSize)}>
          <Typography
            color={theme.presentation.choice.labelColor}
            css={labelTextStyles}
            variant={resolvedSize === 'small' ? 'b4' : 'b3'}
            {...labelTypographyProps}>
            {required && !hideAsterisk && '* '}
            {label}
          </Typography>
          <FormHelperText
            css={styles.formHelperText(theme, resolvedSize, hasError, disabled)}
            disabled={disabled}
            errors={fieldErrors}
            isError={hasError}
            name={name}>
            {helperText}
          </FormHelperText>
        </div>
        {endAdornment && (
          <div css={styles.endAdornment(theme)} data-testid="checkbox-end-adornment">
            {endAdornment}
          </div>
        )}
      </label>
    </div>
  );
};
